import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Heart,
  Share2,
  Check,
  ArrowLeft,
  Bookmark,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import {
  CBT_CATEGORIES,
  CBT_CARDS,
  type CbtCategoryId,
  type CbtThoughtCard,
  getCardsForCategories,
} from "@/lib/cbt-thought-cards-data";
import { useFavorite } from "@/hooks/useFavorite";

export const Route = createFileRoute("/cbt-thought-cards")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "CBT Thought Cards — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Swipeable CBT thought cards: thought records, catastrophizing, all-or-nothing thinking, mind reading, shoulds, emotional reasoning, self-compassion talk, behavioural activation — with a gentle practice for each.",
      },
      { property: "og:title", content: "CBT Thought Cards — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Trauma-informed CBT thought cards with a small practice you can try right now.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/cbt-thought-cards" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/cbt-thought-cards" }],
  }),
  component: withMemberGate(CbtThoughtCardsPage, "CBT Thought Cards"),
});

const FAV_KEY = "rt:fav-cbt-thought-cards";

function CbtThoughtCardsPage() {
  const [selected, setSelected] = useState<CbtCategoryId[]>(() =>
    CBT_CATEGORIES.map((c) => c.id),
  );
  const [deck, setDeck] = useState<CbtThoughtCard[]>(() =>
    getCardsForCategories(CBT_CATEGORIES.map((c) => c.id)),
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setDeck(getCardsForCategories(selected));
    setIndex(0);
  }, [selected]);

  const card = deck[index];
  const nextCard = deck[(index + 1) % Math.max(deck.length, 1)];

  const toggleCategory = (id: CbtCategoryId) => {
    setSelected((prev) => {
      const has = prev.includes(id);
      if (has && prev.length === 1) return prev;
      return has ? prev.filter((c) => c !== id) : [...prev, id];
    });
  };

  const advance = useCallback(() => {
    setIndex((i) => (deck.length ? (i + 1) % deck.length : 0));
  }, [deck.length]);

  return (
    <div className="min-h-dvh bg-[var(--bg-soft,theme(colors.background))]">
      <div className="mx-auto max-w-3xl px-4 pb-24 pt-6 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground/70 hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Daily Support
          </Link>
          <Link
            to="/favorites"
            className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-gold-light/30 px-3 py-1.5 text-xs font-medium text-[var(--gold-deep)]"
          >
            <Bookmark className="h-3.5 w-3.5" />
            Saved
          </Link>
        </div>

        <header className="mb-6 text-center">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[var(--gold-deep)]">
            Daily Support
          </p>
          <h1 className="mt-2 font-display text-3xl text-foreground sm:text-4xl">
            💭 CBT Thought Cards
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground/70">
            Gentle CBT tools for catching unhelpful thoughts and offering
            yourself a kinder, more balanced view. Swipe through and try one
            small practice in the moment.
          </p>
        </header>

        <div className="mb-6 flex flex-wrap justify-center gap-2">
          {CBT_CATEGORIES.map((c) => {
            const on = selected.includes(c.id);
            const count = CBT_CARDS.filter((s) => s.category === c.id).length;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleCategory(c.id)}
                aria-pressed={on}
                className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  on
                    ? "border-transparent bg-foreground text-background"
                    : "border-border bg-card text-foreground/70 hover:text-foreground"
                }`}
              >
                <span aria-hidden>{c.emoji}</span>
                {c.label}
                <span className="opacity-60">· {count}</span>
              </button>
            );
          })}
        </div>

        <Deck card={card} nextCard={nextCard} onSwiped={advance} />

        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={advance}
            className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-cream/40 px-4 py-2 text-sm font-medium text-foreground/80 hover:bg-gold-light/40"
          >
            Next card
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-8 text-center text-[11px] text-foreground/50">
          {deck.length} cards · drag a card or tap Next
        </p>
      </div>
    </div>
  );
}

function Deck({
  card,
  nextCard,
  onSwiped,
}: {
  card: CbtThoughtCard | undefined;
  nextCard: CbtThoughtCard | undefined;
  onSwiped: () => void;
}) {
  if (!card) {
    return (
      <div className="rounded-3xl border border-border bg-card p-10 text-center text-sm text-foreground/60">
        No cards in this selection.
      </div>
    );
  }

  return (
    <div className="relative mx-auto w-full max-w-md">
      {nextCard && nextCard.id !== card.id && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-3 top-3 bottom-[-12px] -z-0 rounded-[28px] border border-gold/20 bg-gradient-to-br from-gold-light/30 via-card to-cream/50 opacity-70 shadow-[var(--shadow-gold)]"
        />
      )}
      <SwipeableCard key={card.id} card={card} onSwiped={onSwiped} />
    </div>
  );
}

function SwipeableCard({
  card,
  onSwiped,
}: {
  card: CbtThoughtCard;
  onSwiped: () => void;
}) {
  const { saved, toggle } = useFavorite(FAV_KEY, card.id);

  const [drag, setDrag] = useState({ x: 0, y: 0, dragging: false });
  const [leaving, setLeaving] = useState<null | "left" | "right">(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const elRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [practising, setPractising] = useState(false);

  useEffect(() => {
    setDrag({ x: 0, y: 0, dragging: false });
    setLeaving(null);
    setPractising(false);
  }, [card.id]);

  const onPointerDown = (e: React.PointerEvent) => {
    const t = e.target as HTMLElement;
    if (t.closest("button") || t.closest("a")) return;
    if (t.closest("[data-no-drag]")) return;
    elRef.current?.setPointerCapture(e.pointerId);
    startRef.current = { x: e.clientX, y: e.clientY };
    setDrag((d) => ({ ...d, dragging: true }));
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!startRef.current) return;
    setDrag({
      x: e.clientX - startRef.current.x,
      y: (e.clientY - startRef.current.y) * 0.25,
      dragging: true,
    });
  };
  const endDrag = () => {
    if (!startRef.current) return;
    const { x } = drag;
    startRef.current = null;
    const threshold = 110;
    if (Math.abs(x) > threshold) {
      setLeaving(x > 0 ? "right" : "left");
      window.setTimeout(() => onSwiped(), 260);
    } else {
      setDrag({ x: 0, y: 0, dragging: false });
    }
  };

  const rotate = drag.x * 0.04;
  const flyX = leaving === "right" ? 800 : leaving === "left" ? -800 : drag.x;
  const flyRot = leaving ? (leaving === "right" ? 20 : -20) : rotate;
  const transition = drag.dragging
    ? "none"
    : "transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 260ms";
  const opacity = leaving ? 0 : 1;

  const onShare = async () => {
    const text = `${card.title} — ${card.quickReminder}\n\nFrom Recovery Trauma™ CBT Thought Cards.`;
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ text, title: `${card.title} · CBT Card` });
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      }
    } catch {
      /* dismissed */
    }
  };

  const cat = CBT_CATEGORIES.find((c) => c.id === card.category)!;
  const swipeHint =
    Math.abs(drag.x) > 30 ? (drag.x > 0 ? "right" : "left") : null;

  return (
    <div
      ref={elRef}
      role="group"
      aria-label={`${card.title} CBT thought card. Drag left or right for next.`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className="relative z-10 rounded-[28px] border border-gold/25 bg-gradient-to-br from-gold-light/40 via-card to-cream/60 p-6 shadow-[var(--shadow-gold)] select-none sm:p-7"
      style={{
        transform: `translate(${flyX}px, ${drag.y}px) rotate(${flyRot}deg)`,
        transition,
        opacity,
        cursor: drag.dragging ? "grabbing" : "grab",
        touchAction: "pan-y",
      }}
    >
      {swipeHint && (
        <span
          aria-hidden
          className={`pointer-events-none absolute top-6 rounded-full border-2 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest ${
            swipeHint === "right"
              ? "end-6 rotate-12 border-[var(--gold-deep)] text-[var(--gold-deep)]"
              : "start-6 -rotate-12 border-foreground/40 text-foreground/40"
          }`}
        >
          Next
        </span>
      )}

      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-background/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
          <span aria-hidden>{cat.emoji}</span>
          {cat.label}
        </span>
        <span className="text-2xl" aria-hidden>
          {card.emoji}
        </span>
      </div>

      <h2 className="mt-3 font-display text-3xl text-foreground sm:text-[34px]">
        {card.title}
      </h2>

      <div
        data-no-drag
        className="mt-4 max-h-[52vh] space-y-4 overflow-y-auto pe-1 text-sm leading-relaxed text-foreground/80"
      >
        <Section title="What Is It?">
          <p>{card.whatIsIt}</p>
        </Section>

        <Section title="When Might It Help?">
          <ul className="space-y-1.5">
            {card.whenItHelps.map((w) => (
              <li key={w} className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 flex-none rounded-full bg-[var(--gold-deep)]" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="How To Practise It">
          <ol className="space-y-1.5">
            {card.howToPractise.map((step, i) => (
              <li key={step} className="flex gap-2">
                <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-foreground text-[10px] font-semibold text-background">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </Section>

        <div className="rounded-2xl border border-gold/30 bg-background/50 p-3 text-center text-[13px] italic text-foreground/80">
          “{card.quickReminder}”
        </div>

        <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-gold-light/40 to-cream/60 p-4">
          <button
            type="button"
            onClick={() => setPractising((p) => !p)}
            className="flex w-full items-center justify-between gap-2 text-start text-sm font-semibold text-foreground"
          >
            <span className="inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[var(--gold-deep)]" />
              💛 Try This Now
            </span>
            <span className="text-xs font-normal text-foreground/60">
              {practising ? "Hide" : "Open"}
            </span>
          </button>

          {practising && (
            <div className="mt-3 space-y-2 text-sm text-foreground/80">
              <p className="font-medium text-foreground">
                {card.practice.title}
              </p>
              <ol className="space-y-1.5">
                {card.practice.steps.map((step, i) => (
                  <li key={step} className="flex gap-2">
                    <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full border border-[var(--gold-deep)] text-[10px] font-semibold text-[var(--gold-deep)]">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              <p className="pt-1 text-[11px] text-foreground/55">
                Go gently. One thought at a time is enough.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => toggle({ label: card.title, sub: cat.label })}
          aria-pressed={saved}
          aria-label={saved ? "Remove from favourites" : "Save to favourites"}
          className={`inline-flex h-11 w-11 items-center justify-center rounded-full border transition-colors ${
            saved
              ? "border-transparent bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)]"
              : "border-gold/40 bg-background/60 text-[var(--gold-deep)] hover:bg-gold-light/40"
          }`}
        >
          <Heart
            className={`h-5 w-5 ${saved ? "fill-current" : ""}`}
            strokeWidth={2.25}
          />
        </button>

        <button
          type="button"
          onClick={() => onSwiped()}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-foreground px-4 py-2.5 text-sm font-semibold text-background hover:opacity-90"
        >
          Next card
        </button>

        <button
          type="button"
          onClick={onShare}
          aria-label="Share this card"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 bg-background/60 text-[var(--gold-deep)] hover:bg-gold-light/40"
        >
          {copied ? <Check className="h-5 w-5" /> : <Share2 className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--gold-deep)]">
        {title}
      </h3>
      {children}
    </div>
  );
}
